import elliptic from 'elliptic';
import { sha256 } from 'js-sha256';

const P256 = new elliptic.ec('p256');
const P256_KEY_SIZE_BYTES = 32;
const P256_SIGNATURE_SIZE_BYTES = 64;
const P256_SPKI_PREFIX_HEX = '3059301306072a8648ce3d020106082a8648ce3d03010703420004';

function bytesToBase64(bytes) {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
}

function bytesToBase64Url(bytes) {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function base64UrlToBytes(value) {
  const base64 = String(value || '')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(String(value || '').length / 4) * 4, '=');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function hexToBytes(hex, fixedLength = null) {
  let normalizedHex = String(hex || '');

  if (normalizedHex.length % 2) {
    normalizedHex = `0${normalizedHex}`;
  }

  if (fixedLength) {
    normalizedHex = normalizedHex.padStart(fixedLength * 2, '0');
  }

  const bytes = new Uint8Array(normalizedHex.length / 2);

  for (let index = 0; index < normalizedHex.length; index += 2) {
    bytes[index / 2] = Number.parseInt(normalizedHex.slice(index, index + 2), 16);
  }

  return bytes;
}

function numberToBytes(value) {
  if (value === 0) {
    return new Uint8Array([0]);
  }

  const bytes = [];
  let nextValue = value;

  while (nextValue > 0) {
    bytes.unshift(nextValue & 0xff);
    nextValue >>= 8;
  }

  return new Uint8Array(bytes);
}

function derLength(length) {
  if (length < 0x80) {
    return new Uint8Array([length]);
  }

  const lengthBytes = numberToBytes(length);

  return concatBytes(new Uint8Array([0x80 | lengthBytes.length]), lengthBytes);
}

function derTagged(tag, content) {
  return concatBytes(new Uint8Array([tag]), derLength(content.length), content);
}

function derSequence(...children) {
  return derTagged(0x30, concatBytes(...children));
}

function derInteger(value) {
  return derTagged(0x02, new Uint8Array([value]));
}

function derOctetString(content) {
  return derTagged(0x04, content);
}

function derBitString(content) {
  return derTagged(0x03, concatBytes(new Uint8Array([0x00]), content));
}

function derObjectIdentifier(hex) {
  return derTagged(0x06, hexToBytes(hex));
}

function concatBytes(...chunks) {
  const totalLength = chunks.reduce((length, chunk) => length + chunk.length, 0);
  const bytes = new Uint8Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    bytes.set(chunk, offset);
    offset += chunk.length;
  });

  return bytes;
}

function fixedLengthBytes(bytes, length) {
  if (bytes.length === length) {
    return bytes;
  }

  if (bytes.length > length) {
    return bytes.slice(bytes.length - length);
  }

  const nextBytes = new Uint8Array(length);
  nextBytes.set(bytes, length - bytes.length);
  return nextBytes;
}

function hexToBase64Url(hex) {
  return bytesToBase64Url(hexToBytes(hex, P256_KEY_SIZE_BYTES));
}

function base64UrlToHex(value) {
  return bytesToHex(base64UrlToBytes(value));
}

function normalizePrivateKeyToken(privateKeyToken) {
  if (!privateKeyToken) {
    throw new Error('Missing biometric private key.');
  }

  if (typeof privateKeyToken === 'object') {
    return privateKeyToken;
  }

  return JSON.parse(privateKeyToken);
}

function ellipticKeyPairToJwk(keyPair) {
  const privateHex = keyPair.getPrivate('hex').padStart(P256_KEY_SIZE_BYTES * 2, '0');
  const publicKey = keyPair.getPublic();
  const xHex = publicKey.getX().toString('hex', P256_KEY_SIZE_BYTES * 2);
  const yHex = publicKey.getY().toString('hex', P256_KEY_SIZE_BYTES * 2);

  return {
    privateJwk: {
      kty: 'EC',
      crv: 'P-256',
      d: hexToBase64Url(privateHex),
      x: hexToBase64Url(xHex),
      y: hexToBase64Url(yHex),
      alg: 'ES256',
      ext: true,
      key_ops: ['sign'],
    },
    publicJwk: {
      kty: 'EC',
      crv: 'P-256',
      x: hexToBase64Url(xHex),
      y: hexToBase64Url(yHex),
      alg: 'ES256',
      ext: true,
      key_ops: ['verify'],
    },
  };
}

function generateEllipticBiometricKeyPair() {
  const keyPair = P256.genKeyPair();

  return {
    ...ellipticKeyPairToJwk(keyPair),
    provider: 'elliptic',
  };
}

export async function generateBiometricKeyPair() {
  return generateEllipticBiometricKeyPair();
}

export function publicJwkToSpkiBase64(publicJwk) {
  if (!publicJwk?.x || !publicJwk?.y) {
    throw new Error('Invalid biometric public key.');
  }

  const prefix = hexToBytes(P256_SPKI_PREFIX_HEX);
  const x = fixedLengthBytes(base64UrlToBytes(publicJwk.x), P256_KEY_SIZE_BYTES);
  const y = fixedLengthBytes(base64UrlToBytes(publicJwk.y), P256_KEY_SIZE_BYTES);

  return bytesToBase64(concatBytes(prefix, x, y));
}

function base64ToPem(base64, label) {
  const lines = String(base64 || '').match(/.{1,64}/g) || [];

  return [
    `-----BEGIN ${label}-----`,
    ...lines,
    `-----END ${label}-----`,
  ].join('\n');
}

export function publicJwkToSpkiPem(publicJwk) {
  return base64ToPem(publicJwkToSpkiBase64(publicJwk), 'PUBLIC KEY');
}

export function privateJwkToPkcs8Pem(privateJwk) {
  if (!privateJwk?.d || !privateJwk?.x || !privateJwk?.y) {
    throw new Error('Invalid biometric private key.');
  }

  const privateKey = fixedLengthBytes(base64UrlToBytes(privateJwk.d), P256_KEY_SIZE_BYTES);
  const x = fixedLengthBytes(base64UrlToBytes(privateJwk.x), P256_KEY_SIZE_BYTES);
  const y = fixedLengthBytes(base64UrlToBytes(privateJwk.y), P256_KEY_SIZE_BYTES);
  const publicKey = concatBytes(new Uint8Array([0x04]), x, y);
  const ecPrivateKey = derSequence(
    derInteger(1),
    derOctetString(privateKey),
    derTagged(0xa1, derBitString(publicKey))
  );
  const privateKeyInfo = derSequence(
    derInteger(0),
    derSequence(
      derObjectIdentifier('2a8648ce3d0201'),
      derObjectIdentifier('2a8648ce3d030107')
    ),
    derOctetString(ecPrivateKey)
  );
  return base64ToPem(bytesToBase64(privateKeyInfo), 'PRIVATE KEY');
}

function signWithElliptic(privateJwk, payload) {
  const privateHex = base64UrlToHex(privateJwk.d);
  const keyPair = P256.keyFromPrivate(privateHex, 'hex');
  const payloadHash = sha256.array(payload);
  const signature = keyPair.sign(payloadHash, { canonical: true });
  const rawSignature = new Uint8Array(P256_SIGNATURE_SIZE_BYTES);

  rawSignature.set(hexToBytes(signature.r.toString('hex'), P256_KEY_SIZE_BYTES), 0);
  rawSignature.set(
    hexToBytes(signature.s.toString('hex'), P256_KEY_SIZE_BYTES),
    P256_KEY_SIZE_BYTES
  );

  return bytesToBase64(rawSignature);
}

export async function signBiometricPayload({ privateKeyToken, payload }) {
  const privateJwk = normalizePrivateKeyToken(privateKeyToken);

  if (!privateJwk.d) {
    throw new Error('Invalid biometric private key.');
  }

  return {
    provider: 'elliptic',
    signature: signWithElliptic(privateJwk, payload),
  };
}
